import { clampRect } from '@/core/geometry'
import { drawSourceToImageData, isImageDataSource } from '@/core/image'
import { recognitionMetricKeys, type CaptureProfile, type LocatedMetricRegion, type NormalizedRect, type RecognitionMetricKey } from '@/domain/types'
import type { LayoutDetection, LayoutSearchOptions } from '../types'
import { mapPanelRight } from '../mxdc/map-line'
import templates from './layout-templates.json'

interface Template { width: number; height: number; pixels: number[]; probe: number[][] }
export interface GcwAnchor { x: number; y: number; sx: number; sy: number; error: number }
const offsets = [0, 108, 221]
const widths = [105, 105, 115]
const labels = [templates.hp, templates.mp, templates.exp]
const hudMetrics = ['hp', 'mp', 'exp', 'level', 'jobName'] as const
// The supplied large capture has independently measured horizontal/vertical scaling.
const panelScale = { x: 169 / 108, y: 169 / 108 }
const gray = (f: ImageData, x: number, y: number) => {
  const i = (Math.round(y) * f.width + Math.round(x)) * 4
  return (f.data[i]! + 2 * f.data[i + 1]! + f.data[i + 2]!) / 4
}
function check(options: LayoutSearchOptions) {
  if (options.signal?.aborted) throw new DOMException('GCW layout cancelled', 'AbortError')
  if (options.deadlineAt !== undefined && Date.now() >= options.deadlineAt) throw new DOMException('GCW layout timed out', 'TimeoutError')
}
function error(f: ImageData, t: Template, x: number, y: number, sx: number, sy: number, quick = false): number {
  if (x < 0 || y < 0 || x + t.width * sx > f.width || y + t.height * sy > f.height) return 255
  let sum = 0, n = 0
  for (const p of t.probe) {
    sum += Math.abs(gray(f, x + (p[0]! + .5) * sx - .5, y + (p[1]! + .5) * sy - .5) - p[2]!)
    if (++n >= 6 && sum / n > 80) return 255
  }
  if (quick || sum / n > 65) return sum / n
  sum = 0; n = 0
  for (let yy = 0; yy < t.height; yy++) for (let xx = 0; xx < t.width; xx++) {
    sum += Math.abs(gray(f, x + (xx + .5) * sx - .5, y + (yy + .5) * sy - .5) - t.pixels[yy * t.width + xx]!); n++
  }
  return sum / n
}
function refined(f: ImageData, t: Template, x: number, y: number, sx: number, sy: number): number {
  let best = 255
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) best = Math.min(best, error(f, t, x + dx, y + dy, sx, sy))
  return best
}
function labelError(f: ImageData, a: GcwAnchor, i: number): number {
  return refined(f, labels[i]!, a.x + (offsets[i]! - 1) * a.sx, a.y - 13 * a.sy, a.sx, a.sy)
}
function signature(f: ImageData, a: GcwAnchor): boolean {
  // These two toolbar icons at GCW-specific offsets also survive a clipped bottom row.
  return refined(f, templates.shirt, a.x + 398 * a.sx, a.y - 40 * a.sy, a.sx, a.sy) < 48
    && refined(f, templates.arrow, a.x + 550 * a.sx, a.y - 40 * a.sy, a.sx, a.sy) < 48
}
function runs(f: ImageData, y: number): { x: number; width: number }[] {
  const result = []; let start = -1
  for (let x = 0; x <= f.width; x++) {
    const i = (y * f.width + x) * 4
    const bright = x < f.width && Math.min(f.data[i]!, f.data[i + 1]!, f.data[i + 2]!) > 195
    if (bright && start < 0) start = x
    if (!bright && start >= 0) {
      if (x - start >= 40 && x - start <= 480) result.push({ x: start, width: x - start })
      start = -1
    }
  }
  return result
}

export function findGcwHud(f: ImageData, options: LayoutSearchOptions = {}): GcwAnchor[] {
  check(options)
  const found: GcwAnchor[] = []
  for (let y = 0; y < f.height; y++) {
    if (!(y % 16)) check(options)
    const row = runs(f, y)
    for (let i = 0; i < row.length; i++) for (let j = i + 1; j < row.length; j++) {
      const left = row[i]!, right = row[j]!
      if (right.x - left.x > 900) break
      for (const [ia, ib] of [[0, 1], [1, 2], [0, 2]] as const) {
        const sx = (right.x - left.x) / (offsets[ib]! - offsets[ia]!)
        if (sx < .48 || sx > 4.05 || Math.abs(left.width - widths[ia]! * sx) > 4 * Math.max(1, sx)
          || Math.abs(right.width - widths[ib]! * sx) > 4 * Math.max(1, sx)) continue
        const x = left.x - offsets[ia]! * sx
        if (found.some(a => Math.abs(a.x - x) < 5 * sx && Math.abs(a.y - y) < 5 * sx)) continue
        let best: GcwAnchor | undefined
        for (const sy of [...new Set([sx, ...Array.from({ length: 17 }, (_, k) => Math.round((sx * .8 + k * sx * .025) * 1000) / 1000)])]) {
          const a = { x, y, sx, sy, error: 0 }
          const e1 = labelError(f, a, ia)
          if (e1 > 48) continue
          const e2 = labelError(f, a, ib)
          if (e2 > 48 || !signature(f, a)) continue
          a.error = (e1 + e2) / 2
          if (!best || a.error < best.error) best = a
        }
        if (best) found.push(best)
      }
    }
    // Video compression can tint/split two of the white borders. A surviving
    // border proposes geometry only; all three labels AND the toolbar must agree.
    for (const run of row) for (let i = 0; i < widths.length; i++) {
      const sx = run.width / widths[i]!, x = run.x - offsets[i]! * sx
      if (sx < .7 || sx > 4.05 || found.some(a => Math.abs(a.x - x) < 5 * sx && Math.abs(a.y - y) < 5 * sx)) continue
      const a = { x, y, sx, sy: sx, error: 0 }
      const first = labelError(f, a, 0)
      if (first >= 42) continue
      const second = labelError(f, a, 1)
      if (second >= 42) continue
      const third = labelError(f, a, 2)
      if (third >= 42 || !signature(f, a)) continue
      a.error = (first + second + third) / 3
      found.push(a)
    }
  }
  return found
}
function region(f: ImageData, x: number, y: number, width: number, height: number): LocatedMetricRegion | undefined {
  // Do not clamp a cut-off digit into an apparently valid text region.
  if (x < 0 || y < 0 || x + width > f.width + .5 || y + height > f.height + .5) return undefined
  const rect = clampRect({ x: x / f.width, y: y / f.height, width: width / f.width, height: height / f.height })
  return { rect, textRect: rect, confidence: .95 }
}
export function gcwHudRegions(f: ImageData, a: GcwAnchor): LayoutDetection {
  const { x, y, sx, sy } = a, regions: LayoutDetection['regions'] = {}
  for (const [i, metric] of (['hp', 'mp', 'exp'] as const).entries()) {
    if (labelError(f, a, i) > 48) continue
    const text = region(f, x + (offsets[i]! - 2) * sx, y - 14 * sy, (widths[i]! + 2) * sx, 14 * sy)
    if (text) regions[metric] = text
  }
  for (const [metric, dx, dy, w, h] of [['level', -209, -9, 64, 19], ['jobName', -136, -14, 125, 13]] as const) {
    const text = region(f, x + dx * sx, y + dy * sy, w * sx, h * sy)
    if (text) regions[metric] = text
  }
  return { hud: clampRect({ x: (x - 219 * sx) / f.width, y: (y - 19 * sy) / f.height, width: 800 * sx / f.width, height: 37 * sy / f.height }), regions,
    diagnostics: { durationMs: 0, candidates: 1, anchors: 4, scaleX: sx, scaleY: sy } }
}
function anchor(region: LocatedMetricRegion, f: { width: number; height: number }, i: number): GcwAnchor {
  const r = region.textRect ?? region.rect
  const sx = r.width * f.width / (widths[i]! + 2), sy = r.height * f.height / 14
  return { x: r.x * f.width - (offsets[i]! - 2) * sx, y: r.y * f.height + 14 * sy, sx, sy, error: 0 }
}
function findLabels(f: ImageData, t: Template, scales: { sx: number; sy: number }[], options: LayoutSearchOptions,
  bounds = { x: 0, y: 0, width: f.width, height: f.height }): GcwAnchor[] {
  const found: GcwAnchor[] = []
  for (const { sx, sy } of scales) for (let y = Math.max(0, Math.floor(bounds.y)); y <= Math.min(f.height - t.height * sy, bounds.y + bounds.height); y++) {
    if (!(y % 16)) check(options)
    for (let x = Math.max(0, Math.floor(bounds.x)); x <= Math.min(f.width - t.width * sx, bounds.x + bounds.width); x++) {
      if (error(f, t, x, y, sx, sy, true) > 38) continue
      const e = error(f, t, x, y, sx, sy)
      if (e > 35) continue
      const previous = found.find(a => Math.abs(a.x - x) < t.width * sx && Math.abs(a.y - y) < t.height * sy)
      if (!previous) found.push({ x, y, sx, sy, error: e })
      else if (e < previous.error) Object.assign(previous, { x, y, sx, sy, error: e })
    }
  }
  return found
}
interface LabelSearch { template: Template; sx: number; sy: number }
/** Compile the integer-position probes once. Scores and rejection thresholds are
 * identical to findLabels; every position still participates in uniqueness checks. */
function scanLabels(f: ImageData, pixels: Float32Array, searches: LabelSearch[], options: LayoutSearchOptions): GcwAnchor[][] {
  const compiled = searches.map(({ template: t, sx, sy }) => ({
    t, sx, sy, right: Math.floor(f.width - t.width * sx), bottom: Math.floor(f.height - t.height * sy),
    offsets: t.probe.map(p => Math.round((p[1]! + .5) * sy - .5) * f.width + Math.round((p[0]! + .5) * sx - .5)),
    values: t.probe.map(p => p[2]!), found: [] as GcwAnchor[],
  }))
  for (let y = 0; y < f.height; y++) {
    if (!(y % 8)) check(options)
    for (const c of compiled) {
      if (y > c.bottom) continue
      for (let x = 0; x <= c.right; x++) {
        const start = y * f.width + x
        let sum = 0, rejected = false
        for (let n = 0; n < c.offsets.length; n++) {
          sum += Math.abs(pixels[start + c.offsets[n]!]! - c.values[n]!)
          if (n >= 5 && sum / (n + 1) > 80) { rejected = true; break }
        }
        if (rejected || sum / c.offsets.length > 38) continue
        const e = error(f, c.t, x, y, c.sx, c.sy)
        if (e > 35) continue
        const previous = c.found.find(a => Math.abs(a.x - x) < c.t.width * c.sx && Math.abs(a.y - y) < c.t.height * c.sy)
        const value = { x, y, sx: c.sx, sy: c.sy, error: e }
        if (!previous) c.found.push(value)
        else if (e < previous.error) Object.assign(previous, value)
      }
    }
  }
  check(options)
  return compiled.map(c => c.found)
}
const inventories = [
  { title: templates.nativeInventory, coin: templates.nativeCoin, currency: templates.nativeCurrency,
    dx: 16, dy: 260, width: 144, height: 16, coinX: -1, currencyX: 163, currencyY: 262, hudScale: 1 },
  { title: templates.inventory, coin: templates.coin, currency: templates.currency,
    dx: 24, dy: 406, width: 222, height: 25, coinX: -2, currencyX: 252, currencyY: 409, hudScale: 169 / 108 },
]
type InventoryTemplate = typeof inventories[number]
const inventoryOrder = (scale: number) => [...inventories].sort((a, b) => Math.abs(scale / a.hudScale - 1) - Math.abs(scale / b.hudScale - 1))
function inventoryMatches(f: ImageData, p: GcwAnchor, t: InventoryTemplate): boolean {
  return refined(f, t.title, p.x, p.y, p.sx, p.sy) < 42
    && refined(f, t.coin, p.x + t.coinX * p.sx, p.y + t.dy * p.sy, p.sx, p.sy) < 45
    && refined(f, t.currency, p.x + t.currencyX * p.sx, p.y + t.currencyY * p.sy, p.sx, p.sy) < 45
}
function inventoryRegion(f: ImageData, p: GcwAnchor, t: InventoryTemplate) {
  return region(f, p.x + t.dx * p.sx, p.y + t.dy * p.sy, t.width * p.sx, t.height * p.sy)
}
function inventorySelectionMatches(f: ImageData, p: GcwAnchor, t: InventoryTemplate, area: NormalizedRect): boolean {
  const x = area.x * f.width, y = area.y * f.height
  const left = p.x + t.dx * p.sx, top = p.y + t.dy * p.sy
  return x < left + t.width * p.sx && x + area.width * f.width > left
    && y < top + t.height * p.sy && y + area.height * f.height > top
    && x >= p.x + (t.coinX - 4) * p.sx
    && x + area.width * f.width <= p.x + (t.currencyX + t.currency.width + 4) * p.sx
    && y >= top - 4 * p.sy && y + area.height * f.height <= top + (t.height + 4) * p.sy
}
/** A text selection is a search hint, never a ruler for the entire inventory. */
export function findGcwInventoryNear(f: ImageData, area: NormalizedRect, hudScale: number | Pick<GcwAnchor, 'sx' | 'sy'> = 1): LocatedMetricRegion | undefined {
  const hud = typeof hudScale === 'number' ? { sx: hudScale, sy: hudScale } : hudScale
  for (const t of inventoryOrder(hud.sx)) {
    const sx = area.width * f.width / t.width, sy = area.height * f.height / t.height
    if (sx / sy < .8 || sx / sy > 1.2) continue
    const p = { x: area.x * f.width - t.dx * sx, y: area.y * f.height - t.dy * sy, sx, sy, error: 0 }
    if (inventoryMatches(f, p, t)) return inventoryRegion(f, p, t)
  }
  const ax = area.x * f.width, ay = area.y * f.height, aw = area.width * f.width, ah = area.height * f.height
  for (const t of inventoryOrder(hud.sx)) {
    const sx = hud.sx / t.hudScale, sy = hud.sy / t.hudScale
    const coins = findLabels(f, t.coin, [{ sx, sy }], {},
      { x: ax - (t.width + t.dx) * sx, y: ay - Math.max(ah, 4 * sy),
        width: (t.width + t.dx) * sx + aw, height: 2 * Math.max(ah, 4 * sy) })
    for (const coin of coins) {
      const p = { ...coin, x: coin.x - t.coinX * sx, y: coin.y - t.dy * sy }
      const amount = inventoryRegion(f, p, t)
      if (!amount || !inventoryMatches(f, p, t)) continue
      // Accept digits-only and icon/label-inclusive crops, but never another row.
      if (inventorySelectionMatches(f, p, t, area)) return amount
    }
  }
  return undefined
}
export function gcwInventoryVisible(f: ImageData, area: NormalizedRect, selection?: NormalizedRect): boolean {
  // Saved automatic regions have a known number-field shape; local validation
  // deliberately does not search for a different inventory after it moves.
  return inventories.some(t => {
    const sx = area.width * f.width / t.width, sy = area.height * f.height / t.height
    if (sx / sy < .8 || sx / sy > 1.2) return false
    const p = { x: area.x * f.width - t.dx * sx, y: area.y * f.height - t.dy * sy, sx, sy, error: 0 }
    return (!selection || inventorySelectionMatches(f, p, t, selection)) && inventoryMatches(f, p, t)
  })
}
function mapRegion(f: ImageData, p: GcwAnchor): LocatedMetricRegion | undefined {
  const left = p.x + 67 * p.sx, top = p.y + 58 * p.sy
  const right = mapPanelRight(f, { x: Math.round(left), y: Math.round(top), width: Math.round(252 * p.sx), height: Math.round(23 * p.sy) })
  if (right - left < 23 * p.sx || refined(f, templates.mapHeader, right - 23 * p.sx, p.y + 43 * p.sy, p.sx, p.sy) >= 25) return undefined
  // Keep existing wide-panel crops pixel-for-pixel (including fractional scale).
  const width = Math.abs(right - left - 252 * p.sx) <= Math.max(1, p.sx) ? 252 * p.sx : right - left
  return region(f, left, top, width, 23 * p.sy)
}
function mapVisible(f: ImageData, area: NormalizedRect, hud?: GcwAnchor): boolean {
  const sy = area.height * f.height / 23, sx = hud ? hud.sx / panelScale.x : sy
  const x = area.x * f.width - 67 * sx, y = area.y * f.height - 58 * sy
  if (refined(f, templates.minimap, x, y, sx, sy) >= 42) return false
  const expected = mapRegion(f, { x, y, sx, sy, error: 0 })
  return !!expected && Math.abs(expected.rect.width - area.width) * f.width <= Math.max(1, sx)
}
export function validateGcwLayout(f: ImageData, detection: LayoutDetection): LayoutDetection {
  const all = { ...detection.supportRegions, ...detection.regions }
  const i = ['hp', 'mp', 'exp'].findIndex(m => all[m as 'hp'])
  const a = i >= 0 ? anchor(all[(['hp', 'mp', 'exp'] as const)[i]!]!, f, i) : undefined
  const validHud = a && signature(f, a) && labels.filter((_, j) => labelError(f, a, j) < 48).length >= 2
  const expected = validHud ? gcwHudRegions(f, a).regions : {}
  for (const metric of hudMetrics) if (!expected[metric]) delete all[metric]
  if (all.mapName && !mapVisible(f, all.mapName.rect, validHud ? a : undefined)) delete all.mapName
  if (all.mesos && !gcwInventoryVisible(f, all.mesos.rect)) delete all.mesos
  return { ...detection, supportRegions: all, regions: Object.fromEntries(Object.keys(detection.regions).filter(m => all[m as RecognitionMetricKey]).map(m => [m, all[m as RecognitionMetricKey]])) }
}
export function locateGcwLayout(f: ImageData, metrics: readonly RecognitionMetricKey[] = recognitionMetricKeys, options: LayoutSearchOptions = {}): LayoutDetection {
  check(options)
  const started = performance.now(), regions: LayoutDetection['regions'] = {}
  const hint = options.hint ? validateGcwLayout(f, options.hint) : undefined
  const hintRegions = { ...hint?.supportRegions, ...hint?.regions }
  const hintIndex = ['hp', 'mp', 'exp'].findIndex(m => hintRegions[m as 'hp'])
  const hinted = hintIndex < 0 ? undefined : anchor(hintRegions[(['hp', 'mp', 'exp'] as const)[hintIndex]!]!, f, hintIndex)
  const anchors = metrics.some(m => (hudMetrics as readonly string[]).includes(m))
    || metrics.includes('mesos') && !hinted ? findGcwHud(f, options) : []
  if (anchors.length > 1) return { regions, diagnostics: { reason: 'multiple-scenes', candidates: anchors.length, durationMs: performance.now() - started } }
  const a = anchors[0] ?? hinted, hud = a ? gcwHudRegions(f, a) : undefined
  if (hud) for (const metric of hudMetrics) if (metrics.includes(metric) && hud.regions[metric]) regions[metric] = hud.regions[metric]
  const scales = a ? [{ sx: a.sx / panelScale.x, sy: a.sy / panelScale.y }]
    : [1, .64, .8, 1.28, 1.6, 1.92, 2.56, .32, .48].map(s => ({ sx: s, sy: s }))
  const reasons: Partial<Record<RecognitionMetricKey, 'timeout' | 'not-found'>> = {}
  let pixels: Float32Array | undefined, mesosSearchMs = 0
  const grayscale = (search: LayoutSearchOptions) => {
    if (!pixels) {
      const data = new Float32Array(f.width * f.height)
      for (let y = 0; y < f.height; y++) {
        if (!(y % 16)) check(search)
        for (let x = 0; x < f.width; x++) {
          const p = y * f.width + x, i = p * 4
          data[p] = (f.data[i]! + 2 * f.data[i + 1]! + f.data[i + 2]!) / 4
        }
      }
      pixels = data
    }
    return pixels
  }
  for (const metric of ['mapName', 'mesos'] as const) {
    if (!metrics.includes(metric)) continue
    const metricStarted = performance.now()
    try {
      if (metric === 'mesos' && options.mesosCandidate) {
        if (gcwInventoryVisible(f, options.mesosCandidate.rect)) regions.mesos = options.mesosCandidate
        continue
      }
      if (metric === 'mesos' && hintRegions.mesos && gcwInventoryVisible(f, hintRegions.mesos.rect)) {
        regions.mesos = hintRegions.mesos
        continue
      }
      const deadlineAt = Math.min(options.deadlineAt ?? Infinity, Date.now() + (metric === 'mesos' ? options.mesosBudgetMs ?? 1_000 : 2_000))
      const search = { ...options, deadlineAt }
      check(search)
      const inventorySearches = inventoryOrder(a?.sx ?? 1).flatMap(t => (a ? [{ sx: a.sx / t.hudScale, sy: a.sy / t.hudScale }]
        : [1, .75, 1.5, 2, .64].map(s => ({ sx: s, sy: s }))).map(scale => ({ ...scale, template: t.title, inventory: t })))
      const candidates = (metric === 'mesos'
        ? scanLabels(f, grayscale(search), inventorySearches, search).flatMap((found, index) => {
          const t = inventorySearches[index]!.inventory
          return found.flatMap(p => {
            // HUD height estimates can drift while the inventory remains uniformly scaled.
            // Recheck only this title candidate; keep all three structural checks.
            const uniform = { ...p, sy: p.sx }
            const verified = inventoryMatches(f, p, t) ? p
              : inventoryMatches(f, uniform, t) ? uniform : null
            return verified ? [inventoryRegion(f, verified, t)] : []
          })
        })
        : scanLabels(f, grayscale(search), scales.map(scale => ({ ...scale, template: templates.minimap })), search)
          .flatMap(found => found.map(p => mapRegion(f, p))))
        .filter((r): r is LocatedMetricRegion => !!r && (metric === 'mesos' ? gcwInventoryVisible(f, r.rect) : mapVisible(f, r.rect, a)))
      check(search)
      const unique = candidates.filter((r, i) => !candidates.slice(0, i).some(p => Math.abs(p.rect.x - r.rect.x) * f.width < 8
        && Math.abs(p.rect.y - r.rect.y) * f.height < 8))
      if (unique.length === 1) regions[metric] = unique[0]
    } catch (e) {
      if (!(e instanceof DOMException) || e.name !== 'TimeoutError' || options.signal?.aborted) throw e
      reasons[metric] = 'timeout'
    } finally {
      if (metric === 'mesos') mesosSearchMs = performance.now() - metricStarted
    }
  }
  return { hud: hud?.hud, regions, supportRegions: hud?.regions, diagnostics: { ...hud?.diagnostics, durationMs: performance.now() - started, mesosSearchMs, candidates: anchors.length, reasons } }
}
export function validGcwBar(source: CanvasImageSource | ImageData, width: number, height: number, metric: 'hp' | 'mp' | 'exp', r: LocatedMetricRegion): boolean {
  const i = ['hp', 'mp', 'exp'].indexOf(metric), a = anchor(r, { width, height }, i)
  if (isImageDataSource(source)) return labelError(source, a, i) < 48 && signature(source, a)
  const bounds = clampRect({ x: (a.x - 3 * a.sx) / width, y: (a.y - 42 * a.sy) / height, width: 580 * a.sx / width, height: 43 * a.sy / height })
  const f = drawSourceToImageData(source, width, height, bounds)
  return labelError(f, { ...a, x: a.x - Math.round(bounds.x * width), y: a.y - Math.round(bounds.y * height) }, i) < 48
    && signature(f, { ...a, x: a.x - Math.round(bounds.x * width), y: a.y - Math.round(bounds.y * height) })
}
export function locatedGcwJobRect(profile: CaptureProfile): NormalizedRect | undefined { return profile.autoRois?.jobName?.rect }
